import { WEEKDAYS_SHORT } from "@customer-app/account/views/OperationAquarius/AquariusInner/components/OrderHistory/utils";
import { useDisabledDatesByBillingDays } from "@customer-app/account/views/OperationAquarius/hooks/useDisabledDatesByBillingDays";

import { IconButton, Stack, SxProps, Theme } from "@mui/material";
import "react-day-picker/lib/style.css";

// Components

// Utils & Helpers
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AvailableLanguages, ICustomerPortalTheme } from "@smartrr/shared/entities/CustomerPortalTheme";
import { isDayInArrOfDates } from "@smartrr/shared/utils/dateUtils";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { ArrowBack } from "akar-icons";
import moment from "moment";
import React from "react";
import DayPicker from "react-day-picker";
import styled from "styled-components";

import { useSmartrrTheme } from "@customer-app/account/views/OperationAquarius/hooks/useSmartrrTheme";

export interface AquariusDatePickerProps {
  /** Calendar labels
   * @default S,M,T,W,T,F,S
   */
  weekdaysShort?: string[];

  selectedTime: Date | null;

  /** Setter for selected date */
  setSelectedTime: (value: React.SetStateAction<Date | null>) => void;

  /** Month calendar begins on */
  initialMonth?: Date | undefined;

  /** Days before x will be disabled */
  disabledDaysBefore: Date;

  /** Days after x will be disabled */
  disabledDaysAfter?: Date;

  /** Callback function called when date is selected */
  selectDate: (date: Date, index: number) => void;

  /** List of other delay date button */
  delayButtons?: {
    label: string;
  }[];

  currentDeliveryDate?: Date;

  sx?: SxProps<Theme>;
}

const StyledDatePicker = styled(DayPicker).attrs(props => ({
  ...props,
  className: prefixClassName("date-picker"),
}))<{ aquariusTheme: ICustomerPortalTheme }>`
  display: flex;
  justify-content: center;
  & .DayPicker-wrapper {
    padding: 0px;
    margin-bottom: 15px;
    max-width: 265px;
    & .DayPicker-Month {
      width: 223px !important;
    }
    & .DayPicker-WeekdaysRow {
      display: flex;
      justify-content: space-between;
      & .DayPicker-Weekday {
        color: ${props => props.aquariusTheme.modernBodyFontColor};
      }
    }
    & .DayPicker-Week {
      display: flex;
      flex-direction: row;
      &:not(:first-of-type) {
        margin-top: 8px;
      }
    }
    & .DayPicker-Caption {
      text-align: center;
      text-transform: uppercase;
      font-family: ${props => props.aquariusTheme.modernSubHeadingFontFamily};
      font-size: ${props => props.aquariusTheme.modernSubHeadingFontSize}px;
      color: ${props => props.aquariusTheme.modernSubHeadingFontColor};
    }
    & .DayPicker-Day {
      border-radius: 10px;
      border: 1px solid ${props => props.aquariusTheme.modernSecondaryButtonBorderColor};
      padding: 4px 9px;
      max-width: 25px;
      width: 25px;
      font-size: 12px;
      display: flex;
      justify-content: center;
      max-height: 25px;
      align-items: center;
      transition: 0.3s ease-in-out;
      white-space: nowrap;
      &:not(:first-of-type) {
        margin-left: 8px;
      }
    }
    & .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside) {
      transition: 0.3s ease-in-out;
      background-color: ${props => props.aquariusTheme.modernPrimaryButtonColor};
      color: ${props => props.aquariusTheme.modernPrimaryButtonFontColor};
    }
    & .DayPicker-Day--outside {
      border: 1px solid ${props => props.aquariusTheme.modernSecondaryButtonBorderColor};
      background: rgba(0, 0, 0, 0.1);
    }
  }
`;
export const AquariusDatePicker = ({
  weekdaysShort = ["S", "M", "T", "W", "T", "F", "S"],
  selectedTime,
  setSelectedTime,
  initialMonth,
  disabledDaysBefore,
  disabledDaysAfter,
  selectDate,
  delayButtons,
  currentDeliveryDate,
  sx,
  ...props
}: AquariusDatePickerProps): JSX.Element => {
  const now = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(now.getDate() + 1);
  const theme = useSmartrrTheme();

  const activeLanguage = theme.translationOverrides?.activeLanguage ?? AvailableLanguages.ENGLISH;
  const disabledDatesFromBilling = useDisabledDatesByBillingDays(selectedTime || initialMonth || now);

  const months = [
    typedLocalize("defaults.dates.months.January"),
    typedLocalize("defaults.dates.months.February"),
    typedLocalize("defaults.dates.months.March"),
    typedLocalize("defaults.dates.months.April"),
    typedLocalize("defaults.dates.months.May"),
    typedLocalize("defaults.dates.months.June"),
    typedLocalize("defaults.dates.months.July"),
    typedLocalize("defaults.dates.months.August"),
    typedLocalize("defaults.dates.months.September"),
    typedLocalize("defaults.dates.months.October"),
    typedLocalize("defaults.dates.months.November"),
    typedLocalize("defaults.dates.months.December"),
  ];
  return (
    <Stack
      spacing={"15px"}
      direction="column"
      sx={{ display: "flex", justifyContent: "center", ...sx }}
      {...props}
    >
      <StyledDatePicker
        aquariusTheme={theme}
        numberOfMonths={1}
        months={months}
        weekdaysShort={WEEKDAYS_SHORT[activeLanguage as keyof typeof WEEKDAYS_SHORT]}
        initialMonth={initialMonth}
        selectedDays={selectedTime ? selectedTime : tomorrow}
        fromMonth={disabledDaysBefore}
        toMonth={disabledDaysAfter}
        firstDayOfWeek={activeLanguage === AvailableLanguages.ENGLISH ? 0 : 1}
        enableOutsideDaysClick={false}
        onMonthChange={setSelectedTime}
        locale={activeLanguage}
        month={selectedTime!}
        disabledDays={[
          ...disabledDatesFromBilling,
          currentDeliveryDate,
          disabledDaysBefore
            ? {
                before: disabledDaysBefore,
              }
            : undefined,
          disabledDaysAfter
            ? {
                after: disabledDaysAfter,
              }
            : undefined,
        ]}
        onDayClick={date => {
          const d = moment(date);
          // if selected date is within disabled billing days, no action is taken
          if (
            isDayInArrOfDates(disabledDatesFromBilling, date) ||
            //moment defaults to current time when disabledDays... is undefined
            (disabledDaysBefore ? d.isBefore(disabledDaysBefore) : false) ||
            (disabledDaysAfter ? d.isAfter(disabledDaysAfter) : false)
          ) {
            return;
          }

          selectDate(date, delayButtons ? delayButtons.length - 1 : 0);
        }}
      />
      {selectedTime?.getMonth() === now.getMonth() ? (
        <React.Fragment />
      ) : (
        <IconButton
          onClick={() => setSelectedTime(tomorrow)}
          aria-label="return to current month"
          component="div"
          sx={{ width: "max-content", margin: "0 auto 15px auto !important" }}
          data-testid="return-icon-button"
        >
          <ArrowBack strokeWidth={2} size={16} color={theme.modernIconsColor} />
        </IconButton>
      )}
    </Stack>
  );
};
